import React, { useEffect, useState } from 'react';
import { api, type AnalyticsOverview } from '../lib/api';
import { MetricCard } from '../components/MetricCard';
import { SentimentBadge } from '../components/SentimentBadge';
import { LineChart, Line, BarChart, Bar, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export const Overview: React.FC = () => {
    const [data, setData] = useState<AnalyticsOverview | null>(null);
    const [loading, setLoading] = useState(true);
    const [trendDays, setTrendDays] = useState<7 | 14 | 30>(30);

    useEffect(() => {
        loadData();
        // Auto-refresh every 30 seconds
        const interval = setInterval(loadData, 30000);
        return () => clearInterval(interval);
    }, []);

    const loadData = async () => {
        try {
            const overview = await api.getAnalyticsOverview();
            setData(overview);
        } catch (error) {
            console.error('Failed to load overview:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading || !data) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-pulse text-gray-500">Loading analytics...</div>
            </div>
        );
    }

    const sentimentPercentage = Math.round(((data.sentimentAvg + 1) / 2) * 100);

    // Prepare trend data based on selected period
    const trendData = data.sentimentTrend.slice(-trendDays);

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gradient">Feedback Overview</h1>
                    <p className="text-gray-600 mt-1">Real-time insights from all feedback sources</p>
                </div>
                <button onClick={loadData} className="btn-primary">
                    <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                </button>
            </div>

            {/* Hero Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard
                    title="Total Feedback (30d)"
                    value={data.totalFeedback30d}
                    icon={
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                    }
                />

                <MetricCard
                    title="Average Sentiment"
                    value={`${sentimentPercentage}%`}
                    trend={sentimentPercentage > 60 ? 'up' : sentimentPercentage < 40 ? 'down' : 'neutral'}
                    icon={
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    }
                />

                <MetricCard
                    title="Top Category"
                    value={data.topCategory.replace('-', ' ')}
                    icon={
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                    }
                />

                <MetricCard
                    title="Urgent Items"
                    value={data.urgentCount}
                    trend={data.urgentCount > 5 ? 'down' : 'neutral'}
                    icon={
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    }
                />
            </div>

            {/* Sentiment Trend Chart */}
            <div className="card">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold">Sentiment Trend</h2>
                    <div className="flex gap-2">
                        {[7, 14, 30].map(days => (
                            <button
                                key={days}
                                onClick={() => setTrendDays(days as 7 | 14 | 30)}
                                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${trendDays === days
                                    ? 'bg-primary-100 text-primary-700'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                {days}d
                            </button>
                        ))}
                    </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" stroke="#6b7280" />
                        <YAxis domain={[-1, 1]} stroke="#6b7280" />
                        <Tooltip />
                        <Line
                            type="monotone"
                            dataKey="score"
                            stroke="#3b82f6"
                            strokeWidth={3}
                            dot={{ fill: '#3b82f6', r: 4 }}
                            activeDot={{ r: 6 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Theme Distribution */}
                <div className="card">
                    <h2 className="text-xl font-bold mb-4">Theme Distribution</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={data.themeDistribution}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="category" stroke="#6b7280" />
                            <YAxis stroke="#6b7280" />
                            <Tooltip />
                            <Bar dataKey="count" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Urgency vs Value */}
                <div className="card">
                    <h2 className="text-xl font-bold mb-4">Urgency vs Business Value</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <ScatterChart>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis type="number" dataKey="urgency" name="Urgency" domain={[0, 5]} stroke="#6b7280" />
                            <YAxis type="number" dataKey="value" name="Value" domain={[0, 5]} stroke="#6b7280" />
                            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                            <Scatter name="Feedback" data={data.urgencyValueScatter} fill="#3b82f6" />
                        </ScatterChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Recent Feedback Stream */}
            <div className="card">
                <h2 className="text-xl font-bold mb-4">Recent Feedback</h2>
                <div className="space-y-3">
                    {data.themeDistribution.slice(0, 5).map((theme, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="flex-1">
                                <div className="font-medium capitalize">{theme.category.replace('-', ' ')}</div>
                                <div className="text-sm text-gray-600">{theme.count} items</div>
                            </div>
                            <SentimentBadge
                                sentiment={
                                    theme.sentiment_avg > 0.3 ? 'positive' :
                                        theme.sentiment_avg < -0.3 ? 'negative' :
                                            'neutral'
                                }
                                score={theme.sentiment_avg}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
